import { useEffect, useState, type ReactNode } from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { getYandexMapKeys } from '@noor/api';
import { YmapsContext, type YmapsBindings, type YmapsContextValue } from '@noor/maps';

/**
 * Loads the Yandex Maps v3 (ymaps3) SDK once via a CDN script, reactifies it,
 * and exposes the bindings through `YmapsContext`. Ported from cpanel's
 * `YMapsProvider` (Redux → React context; the SDK handle is infra/server-ish
 * state, NOT Zustand UI state). Singleton-guarded so React StrictMode's double
 * effect doesn't inject the script twice. When neither the backend nor env can
 * provide a key, the provider is disabled and the map degrades to a placeholder.
 */

let loadPromise: Promise<YmapsBindings> | null = null;

async function loadYmaps(apiKey: string): Promise<YmapsBindings> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<YmapsBindings>((resolve, reject) => {
    if (window.ymaps3) {
      void initBindings().then(resolve).catch(reject);
      return;
    }
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`;
    script.onload = () => {
      void initBindings().then(resolve).catch(reject);
    };
    script.onerror = () => reject(new Error('Failed to load Yandex Maps SDK'));
    document.body.appendChild(script);
  });
  return loadPromise;
}

async function initBindings(): Promise<YmapsBindings> {
  const ymaps = window.ymaps3;
  if (!ymaps) throw new Error('ymaps3 not present after load');
  await ymaps.ready;
  ymaps.import.registerCdn('https://cdn.jsdelivr.net/npm/{package}', [
    '@yandex/ymaps3-default-ui-theme@0.0',
  ]);
  const reactifyModule = (await ymaps.import('@yandex/ymaps3-reactify')) as {
    reactify: {
      bindTo: (r: typeof React, rd: typeof ReactDOM) => YmapsBindings['reactify'];
    };
  };
  const reactify = reactifyModule.reactify.bindTo(React, ReactDOM);
  return { ymaps3: ymaps, reactify };
}

export function YmapsProvider({ children }: { children: ReactNode }) {
  // The key is resolved at runtime from the map-keys endpoint. Do not disable
  // the provider based only on the optional auth env flag: deployments may
  // expose the endpoint through the app gateway without that client-side flag.
  const [isDisabled, setIsDisabled] = useState(false);
  const [bindings, setBindings] = useState<YmapsBindings | null>(null);

  useEffect(() => {
    let active = true;
    const timeout = window.setTimeout(() => {
      if (active) setIsDisabled(true);
    }, 15000);
    getYandexMapKeys()
      .then((keys) => {
        if (!keys.yandex) {
          if (active) setIsDisabled(true);
          return null;
        }
        return loadYmaps(keys.yandex);
      })
      .then((b) => {
        if (active && b) {
          window.clearTimeout(timeout);
          setBindings(b);
          setIsDisabled(false);
        }
      })
      .catch((error: unknown) => {
        console.error('[Yandex Maps] SDK initialization failed', error);
        if (active) setIsDisabled(true);
      });
    return () => {
      active = false;
      window.clearTimeout(timeout);
    };
  }, []);

  const value: YmapsContextValue = {
    bindings,
    isReady: bindings !== null,
    isDisabled,
  };

  return <YmapsContext.Provider value={value}>{children}</YmapsContext.Provider>;
}
